import React, { useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { differenceInDays, format, isSameDay } from 'date-fns';
import clsx from 'clsx';
import ReactDOM from 'react-dom';

import ListingImagesCarousel from '@components/_shared/listingImagesCarousel';

import { useIsMobile } from '@utils/hooks/useIsMobile';
import { toDollars } from '@utils/money';
import { FeaturedListingType } from '@utils/types/featuredListing';
import { DatesRange } from '@utils/types/calendar';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import {
  getFormattedListingAddress,
  listingTitleWithoutByRove,
} from '@utils/helpers';
import {
  calculateNightlyPrice,
  calculateMonthlyPrice,
  getMinStayPrice,
  calculateMinStayPrice,
} from '@utils/pricing';
import { useGetListingLink } from '@utils/hooks/useGetListingLink';
import { AmenityComponent } from '@components/listing/amenities';
import { AmenitiesType } from '@utils/types/amenities';
import { parseDateWithoutTimezone } from '@utils/dates';

import styles from './styles.module.scss';

type Props = {
  listing?: FeaturedListingType;
  dates?: DatesRange;
  listingIDToScrollTo?: string;
};

export function ListingCard(props: Props) {
  const { listing, dates, listingIDToScrollTo } = props;
  const isMobile = useIsMobile();
  const { trackEvent } = useAnalytics();
  const listingRef = useRef<HTMLDivElement>(null);
  const { prepareListingHref } = useGetListingLink();

  const isHighlighted = useMemo(() => {
    return listingIDToScrollTo === listing?.id;
  }, [listingIDToScrollTo, listing?.id]);

  useEffect(() => {
    if (!isMobile && listingRef.current && isHighlighted) {
      listingRef.current.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [isHighlighted]);

  const onListingClick = () => {
    trackEvent(SegmentEventNamesEnum.LISTING_CLICKED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
    });
  };

  const fuzzyDatesAreDifferentFromSelected = useMemo(() => {
    return (
      listing?.closest_start &&
      listing?.closest_end &&
      dates?.from &&
      dates?.to &&
      (!isSameDay(
        dates.from,
        parseDateWithoutTimezone(listing.closest_start)
      ) ||
        !isSameDay(dates.to, parseDateWithoutTimezone(listing.closest_end)))
    );
  }, [listing?.closest_start, listing?.closest_end, dates?.from, dates?.to]);

  const renderPrice = useMemo(() => {
    if (!listing) return null;

    const minStayPrice = calculateMinStayPrice(listing);

    const highestPrice = listing.average_monthly_stay || 0;

    if (!dates || !dates.to || !dates.from) {
      return (
        <div style={{ textAlign: 'right' }}>
          {listing.min_stay < 30 && (
            <div className={styles.roomsAndPrice__label}>Per night</div>
          )}

          {listing.min_stay >= 30 && (
            <div className={styles.roomsAndPrice__label}>
              Avg. price per month
            </div>
          )}

          <div className={styles.roomsAndPrice__value}>
            {listing.min_stay >= 30 && minStayPrice < highestPrice ? (
              <span>{toDollars(highestPrice, 0)}</span>
            ) : null}
            {getMinStayPrice(listing, false)}
          </div>
        </div>
      );
    }

    const activeDates = {
      from: listing.closest_start
        ? parseDateWithoutTimezone(listing.closest_start)
        : dates.from,
      to: listing.closest_end
        ? parseDateWithoutTimezone(listing.closest_end)
        : dates.to,
    };

    const totalDays = differenceInDays(activeDates.to, activeDates.from);

    if (totalDays < listing.min_stay) {
      return (
        <div style={{ textAlign: 'right', maxWidth: '50%' }}>
          <div className={styles.roomsAndPrice__label}>
            This property is only available for {listing.min_stay}+ days. Please
            adjust your dates to see the price.
          </div>
        </div>
      );
    }

    if (totalDays < 30 || listing.min_stay < 30) {
      return (
        <div style={{ textAlign: 'right' }}>
          <div className={styles.roomsAndPrice__label}>Per night</div>
          <div className={styles.roomsAndPrice__value}>
            {toDollars(
              calculateNightlyPrice({
                dates: activeDates,
                price_for_selected_dates: listing.price_for_selected_dates,
              }),
              0
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'right' }}>
        <div className={styles.roomsAndPrice__label}>Avg. price per month</div>
        <div className={styles.roomsAndPrice__value}>
          {listing.min_stay >= 30 && minStayPrice < highestPrice ? (
            <span>{toDollars(highestPrice, 0)}</span>
          ) : null}
          {toDollars(
            calculateMonthlyPrice({
              dates: activeDates,
              price_for_selected_dates: listing.price_for_selected_dates,
            }),
            0
          )}
        </div>
      </div>
    );
  }, [
    dates,
    listing?.min_stay,
    listing?.sixty_days_average_price,
    listing?.price_for_selected_dates,
    listing?.monthly_average_price,
    listing?.average_monthly_stay,
    listing?.closest_start,
    listing?.closest_end,
  ]);

  useEffect(() => {
    if (isHighlighted) {
      setTimeout(() => {
        const mapPrice = document.getElementById('map-listing-price');
        if (mapPrice && renderPrice) {
          ReactDOM.render(renderPrice, mapPrice);
        }
      }, 0);
    }
  }, [isHighlighted]);

  if (!listing) {
    return (
      <div className={styles.propertyCardContainer}>
        <div
          className="skeleton-box"
          style={{ width: '100%', height: '216.66px' }}
        />

        <div className={styles.info}>
          <div className={styles.info__title}>
            <div
              className="skeleton-box"
              style={{ width: '100%', height: '20.8px', borderRadius: '12px' }}
            />
          </div>

          <div className={styles.info__address}>
            <div
              className="skeleton-box"
              style={{ width: '200px', height: '14px', borderRadius: '8px' }}
            />
          </div>
        </div>

        <div className={styles.roomsAndPrice}>
          <div className={styles.roomsAndPrice__rooms}>
            <div>
              <div className={styles.roomsAndPrice__label}>Bedrooms</div>
              <div
                className="skeleton-box"
                style={{ width: '40px', height: '20px', borderRadius: '12px' }}
              />
            </div>

            <hr className={styles.roomsAndPrice__divider} />

            <div>
              <div className={styles.roomsAndPrice__label}>Bathrooms</div>
              <div
                className="skeleton-box"
                style={{ width: '40px', height: '20px', borderRadius: '12px' }}
              />
            </div>
          </div>

          <div>
            <div className={styles.roomsAndPrice__label}>
              Avg price per month
            </div>
            <div
              className="skeleton-box"
              style={{
                width: '80px',
                height: '20px',
                borderRadius: '12px',
                marginLeft: 'auto',
              }}
            />
          </div>
        </div>

        <div className={styles.amenities}>
          <div
            className="skeleton-box"
            style={{ width: '100%', height: '16px', borderRadius: '12px' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        styles.propertyCardContainer,
        isHighlighted && styles.highlighted
      )}
      onClick={onListingClick}
      ref={listingRef}
    >
      <Link
        href={prepareListingHref({
          listingSlug: listing.url_slug,
          preserveQuery: !fuzzyDatesAreDifferentFromSelected,
          newQuery: fuzzyDatesAreDifferentFromSelected
            ? {
                dates: [
                  parseDateWithoutTimezone(
                    listing.closest_start!
                  ).toISOString(),
                  parseDateWithoutTimezone(listing.closest_end!).toISOString(),
                ],
              }
            : undefined,
        })}
        passHref
      >
        <a>
          {!!listing.photos && !!listing.photos[0] && (
            <ListingImagesCarousel
              photos={listing.photos}
              containerStyle={styles.imageContainer}
              first_available_date={
                !dates?.from ? listing.first_available_date : undefined
              }
              rating={listing.rating}
            />
          )}

          <div className={styles.info}>
            <div
              className={styles.info__title}
              title={listing.cms_listing_title}
            >
              {listing.cms_listing_title ||
                listingTitleWithoutByRove(listing.title)}
            </div>

            <div className={styles.info__address} title={listing.address.city}>
              {getFormattedListingAddress(listing.address)}
            </div>
          </div>

          <div className={styles.roomsAndPrice}>
            <div className={styles.roomsAndPrice__rooms}>
              <div>
                <div className={styles.roomsAndPrice__label}>Bedrooms</div>
                <div className={styles.roomsAndPrice__value}>
                  {listing.bedrooms_total}
                </div>
              </div>

              <hr className={styles.roomsAndPrice__divider} />

              <div>
                <div className={styles.roomsAndPrice__label}>Bathrooms</div>
                <div className={styles.roomsAndPrice__value}>
                  {listing.bathrooms}
                </div>
              </div>
            </div>

            {renderPrice}
          </div>

          <div
            className={styles.amenities}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            {[
              'wifi',
              'malin+goetz_soap',
              'espresso_machine',
              'laundry',
              'linens',
              'standing_desk',
              'monitor',
            ].map((amenity, index) => (
              <AmenityComponent
                key={index}
                amenity={amenity as AmenitiesType}
                containerStyle={styles.amenity}
                iconMode
              />
            ))}
          </div>

          {dates?.from && dates?.to && (
            <div className={styles.availableDates}>
              {`Check-in: ${format(
                listing.closest_start
                  ? parseDateWithoutTimezone(listing.closest_start)
                  : dates.from,
                'MMMM d'
              )}`}
              <span>·</span>
              {`Check-out: ${format(
                listing.closest_end
                  ? parseDateWithoutTimezone(listing.closest_end)
                  : dates.to,
                'MMMM d'
              )}`}
            </div>
          )}
        </a>
      </Link>
    </div>
  );
}
